import { useEffect, useRef, useState } from "react";
import { Drawer } from "../../../../../components/drawers/Drawer"
import SignaturePad from "signature_pad";
import { getOwner, useHub } from "src/core/Global";
import { toastError } from "../../../../../components/base/Toast";
import JobInvoice from "../../models/JobInvoice";
import Indicator from "../../../../../components/base/Indicator";
import { API_ROUTES } from "../../../../../core/Constants";

interface IProps {
    invoiceId: string
    token?: string
    mode?: "sign" | "convert"
    /** What the customer is agreeing to — the account's own words, shown above the pad. */
    disclaimer?: string
    title?: string
    buttonText?: string
    onClose: () => void
    onSigned: (inv: JobInvoice) => void
}

export default function MobileJobInvoiceSignature(props: IProps) {

    const hub = useHub();
    const owner = getOwner();

    const [saving, savingSet] = useState<boolean>(false);

    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const padRef = useRef<SignaturePad | null>(null);

    useEffect(() => {
        const t = window.setTimeout(() => {
            const canvas = canvasRef.current;
            if (!canvas) return;

            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = canvas.offsetHeight * ratio;

            const ctx = canvas.getContext("2d");
            ctx?.setTransform(ratio, 0, 0, ratio, 0, 0);

            padRef.current = new SignaturePad(canvas, {
                backgroundColor: "rgb(255,255,255)",
            });
        }, 100);

        return () => {
            window.clearTimeout(t);
            padRef.current?.off();
            padRef.current = null;
        };
    }, [])

    const clearPad = () => padRef.current?.clear();

    const mode = props.mode ?? "sign";

    const submitSignedRequest = (signatureUrl: string) => {
        if (mode === "convert") {
            hub.Post(API_ROUTES.JobInvoices + "esttoinv", {
                id: props.invoiceId,
                signature: signatureUrl
            }, (res: any) => {
                savingSet(false);
                if (res.success && res.data) {
                    props.onSigned(res.data);
                    props.onClose();
                } else {
                    toastError(res.message || "Failed to convert estimate to invoice.");
                }
            })
        } else {
            if (!props.token || !owner?.id) {
                savingSet(false);
                toastError("Missing invoice token.");
                return;
            }
            hub.Post("/base/public/job/invoice/sign", {
                token: props.token,
                id: props.invoiceId,
                ownerUserId: owner.id,
                signature: signatureUrl
            }, (res: any) => {
                savingSet(false);
                if (res.success && res.data) {
                    props.onSigned(res.data);
                    props.onClose();
                } else {
                    toastError(res.message || "Failed to save signature.");
                }
            })
        }
    };

    const saveSignature = async () => {
        if (!padRef.current || padRef.current.isEmpty()) {
            toastError("Please provide a signature first.");
            return;
        }

        savingSet(true);
        try {
            const dataUrl = padRef.current.toDataURL("image/png");
            const fetched = await fetch(dataUrl);
            const blob = await fetched.blob();

            hub.Upload({
                category: "inv-signatures",
                file: blob
            }, (upRes: any) => {
                if (!upRes.success || !upRes.data) {
                    toastError(upRes.message || "Failed to upload signature.");
                    savingSet(false);
                    return;
                }
                submitSignedRequest(upRes.data);
            })
        } catch (e: any) {
            savingSet(false);
            toastError(e?.message || "Failed to save signature.");
        }
    };

    return (
        <Drawer id="drawer-job-invoice-view-signiture"
            title={props.title ?? (mode === "convert" ? "Sign & Convert to Invoice" : "Sign Invoice")}
            onClose={props.onClose}>

            {
                props.disclaimer &&
                <div style={{
                    fontSize: 13,
                    background: "var(--bs-gray-100)",
                    borderRadius: 10,
                    padding: 14,
                    marginBottom: 12,
                    whiteSpace: "pre-line"
                }}>
                    {props.disclaimer}
                </div>
            }

            <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 10 }}>
                Please sign in the box below.
            </div>

            <div
                data-drawer-no-swipe="true"
                style={{
                    borderRadius: 12,
                    border: "1px solid #E5E7EB",
                    overflow: "hidden",
                    background: "#fff"
                }}>
                <canvas
                    ref={canvasRef}
                    style={{
                        width: "100%",
                        height: "360px",
                        display: "block",
                        touchAction: "none"
                    }}
                />
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <button
                    type="button"
                    className="btn btn-light w-50"
                    disabled={saving}
                    onClick={clearPad}>
                    Clear
                </button>
                <button
                    type="button"
                    className="btn btn-primary w-50"
                    disabled={saving}
                    onClick={saveSignature}>
                    {saving ? <Indicator text="Saving..." /> : (props.buttonText ?? (mode === "convert" ? "Sign & Convert" : "Sign & Accept"))}
                </button>
            </div>
        </Drawer>
    )
}
